import { queryOptions, useQuery } from '@tanstack/react-query';
import type { VisualizationType } from '@tempo-io/roadmap-dto';
import { VisualizationApi } from '@/lib/api/loop-visualization/visualization';
import { ONE_HOUR_IN_MS } from '@/lib/constants';
import { loopVisualizationQueryKeys } from '@/lib/query-keys/loop-visualizations';

/**
 * Query options factory for fetching all visualizations for a view.
 */
export const getAllVisualizationsQueryOptions = (viewId: number) =>
  queryOptions({
    queryKey: loopVisualizationQueryKeys.visualization.all(viewId),
    queryFn: () => VisualizationApi.fetchAllVisualizations(viewId),
    enabled: !!viewId,
  });

/**
 * Query options factory for fetching a single visualization by ID.
 */
export const getVisualizationQueryOptions = (viewId: number, visualizationId: number) =>
  queryOptions({
    queryKey: loopVisualizationQueryKeys.visualization.detail(viewId, visualizationId),
    queryFn: () => VisualizationApi.fetchVisualization(viewId, visualizationId),
    enabled: !!viewId && !!visualizationId,
  });

/**
 * Query options factory for fetching a visualization by type.
 */
export const getVisualizationByTypeQueryOptions = (viewId: number, type: VisualizationType) =>
  queryOptions({
    queryKey: loopVisualizationQueryKeys.visualization.byType(viewId, type),
    queryFn: () => VisualizationApi.fetchVisualizationByType(viewId, type),
    refetchOnMount: false,
    staleTime: ONE_HOUR_IN_MS,
    enabled: !!viewId,
  });

export const useAllVisualizations = (viewId: number) => {
  return useQuery(getAllVisualizationsQueryOptions(viewId));
};

export const useVisualizationByType = (
  viewId: number,
  type: VisualizationType,
  options?: { enabled?: boolean },
) => {
  return useQuery({
    ...getVisualizationByTypeQueryOptions(viewId, type),
    enabled: (options?.enabled ?? true) && !!viewId,
  });
};
